/**
 * Projection page historical-data converters.
 *
 * The Projections page loads the same /historical-data payload as the Historical page and
 * reshapes it (HybridHistoricalData) before handing a per-year bundle to ProjectionInputView /
 * SeasonalityModeView. Extracted from pages/Projections.tsx so tests can build the view's
 * props exactly the way the page does.
 */

import { UnitCountsData } from '../services/api';
import { HistoricalData, MonthlyTotals, createEmptyMonthlyTotals } from '../types';

/**
 * Historical data format from get-historical-data API
 */
export interface HybridHistoricalData {
  locationId: number;
  locationName: string;
  year: number;
  accounts: {
    account: string;
    description: string;
    ytdTotal: number;
    monthlyTotals: MonthlyTotals;
  }[];
  docGross: {
    department: string;
    displayName: string;
    sales?: MonthlyTotals;
    cos?: MonthlyTotals;
    gross?: MonthlyTotals;
    ytdSales?: number;
    ytdCos?: number;
    ytdGross: number;
    monthlyGross?: MonthlyTotals;
    // Pre-calculated total gross (includes F&I for vehicle departments)
    totalGross?: MonthlyTotals;
    ytdTotalGross?: number;
    // Operating Profit fields (Gross - Expenses)
    totalExpenses?: MonthlyTotals;
    operatingProfit?: MonthlyTotals;
    ytdTotalExpenses?: number;
    ytdOperatingProfit?: number;
    // Service department pay-type breakdown (Customer/Warranty/Internal/Other)
    serviceGrossByPayType?: {
      customer: { gross: MonthlyTotals; ytdGross: number };
      warranty: { gross: MonthlyTotals; ytdGross: number };
      internal: { gross: MonthlyTotals; ytdGross: number };
      other: { gross: MonthlyTotals; ytdGross: number };
    };
    // Parts department pay-type breakdown (7 categories)
    partsGrossByPayType?: {
      customerParts: { gross: MonthlyTotals; ytdGross: number };
      warrantyParts: { gross: MonthlyTotals; ytdGross: number };
      internalParts: { gross: MonthlyTotals; ytdGross: number };
      counterRetail: { gross: MonthlyTotals; ytdGross: number };
      counterWholesale: { gross: MonthlyTotals; ytdGross: number };
      otherParts: { gross: MonthlyTotals; ytdGross: number };
      inventoryAdjustment: { gross: MonthlyTotals; ytdGross: number };
    };
  }[];
  unitCounts?: {
    newVehicleUnits: Record<string, number>;
    usedVehicleUnits: Record<string, number>;
    totalNewUnits: number;
    totalUsedUnits: number;
    monthlyNewUnits?: MonthlyTotals;
    monthlyUsedUnits?: MonthlyTotals;
    monthlyNewUnitsByBrand?: Record<string, MonthlyTotals>;
    monthlyUsedUnitsByBrand?: Record<string, MonthlyTotals>;
  };
  // Location-level Operating Profit (for seasonality calculation)
  locationOperatingProfit?: {
    ytdTotal: number;
    monthlyTotals: MonthlyTotals;
  };
}

/**
 * Convert API HistoricalData to HybridHistoricalData format
 * Calculates location-level Operating Profit for seasonality (EXCLUDING OTHERS department)
 */
export function convertToHybridData(data: HistoricalData): HybridHistoricalData {
  // Calculate location-level Operating Profit by summing departments, EXCLUDING OTHERS
  // This matches how the projection and forecast calculations exclude OTHERS
  let ytdOperatingProfit = 0;
  const monthlyOperatingProfit = createEmptyMonthlyTotals();
  let hasMonthlyOpProfitData = false;

  const excludedDepts = ['OTHERS', 'MISC'];

  // Build docGross - prefer departmentSummaries data, fallback to raw docGross
  let docGross: HybridHistoricalData['docGross'] = [];

  // HYBRID format - use raw docGross data which now includes operatingProfit
  if ((data as any).docGross) {
    docGross = (data as any).docGross;

    // Extract Operating Profit from docGross (EXCLUDING OTHERS)
    docGross.forEach(dept => {
      if (excludedDepts.includes(dept.department)) return;

      // Use the new operatingProfit field if available
      if (dept.operatingProfit) {
        hasMonthlyOpProfitData = true;
        for (let m = 1; m <= 12; m++) {
          const key = `month${m}` as keyof MonthlyTotals;
          monthlyOperatingProfit[key] += dept.operatingProfit[key] || 0;
        }
        ytdOperatingProfit += dept.ytdOperatingProfit || 0;
      }
    });
  } else if (data.departmentSummaries && data.departmentSummaries.length > 0) {
    // Legacy DOC format - use departmentSummaries
    docGross = data.departmentSummaries.map(dept => ({
      department: dept.department,
      displayName: dept.departmentName,
      sales: dept.monthlyData?.sales,
      cos: dept.monthlyData?.costOfSale,
      gross: dept.monthlyData?.frontGross,
      ytdSales: dept.sales,
      ytdCos: dept.costOfSale,
      ytdGross: dept.totalGross,
      monthlyGross: dept.monthlyData?.totalGross,
    }));

    // Extract Operating Profit from departmentSummaries (EXCLUDING OTHERS)
    data.departmentSummaries.forEach(dept => {
      if (excludedDepts.includes(dept.department)) return;

      ytdOperatingProfit += dept.operatingProfit || 0;

      if (dept.monthlyData?.operatingProfit) {
        hasMonthlyOpProfitData = true;
        for (let m = 1; m <= 12; m++) {
          const key = `month${m}` as keyof MonthlyTotals;
          monthlyOperatingProfit[key] += dept.monthlyData.operatingProfit[key] || 0;
        }
      }
    });
  }

  // Last resort: Use locationSummary if no department-level data available
  if (!hasMonthlyOpProfitData && data.locationSummary?.monthlyData?.operatingProfit) {
    const locOpProfit = data.locationSummary.monthlyData.operatingProfit;
    for (let m = 1; m <= 12; m++) {
      const key = `month${m}` as keyof MonthlyTotals;
      monthlyOperatingProfit[key] = locOpProfit[key] || 0;
    }
    ytdOperatingProfit = data.locationSummary.operatingProfit || 0;
    hasMonthlyOpProfitData = true;
  }

  return {
    locationId: data.locationId,
    locationName: data.locationName,
    year: data.year,
    accounts: data.accounts.map(acc => ({
      account: acc.account,
      description: acc.description || '',
      ytdTotal: acc.ytdTotal,
      monthlyTotals: acc.monthlyTotals || createEmptyMonthlyTotals(),
    })),
    docGross,
    unitCounts: data.unitCounts,
    // Add location-level Operating Profit for seasonality (EXCLUDING OTHERS when possible)
    locationOperatingProfit: hasMonthlyOpProfitData ? {
      ytdTotal: ytdOperatingProfit,
      monthlyTotals: monthlyOperatingProfit,
    } : undefined,
  };
}

/** Convert a hybrid bundle's unit counts to the UnitCountsData shape DOCReportView / the views expect */
export function getUnitCountsData(data: HybridHistoricalData | null): UnitCountsData | null {
  if (!data?.unitCounts) return null;
  return {
    locationId: data.locationId,
    locationName: data.locationName,
    year: data.year,
    reportType: 'YTD',
    newVehicleUnits: data.unitCounts.newVehicleUnits,
    usedVehicleUnits: data.unitCounts.usedVehicleUnits,
    totalNewUnits: data.unitCounts.totalNewUnits,
    totalUsedUnits: data.unitCounts.totalUsedUnits,
    monthlyNewUnits: data.unitCounts.monthlyNewUnits,
    monthlyUsedUnits: data.unitCounts.monthlyUsedUnits,
    monthlyNewUnitsByBrand: data.unitCounts.monthlyNewUnitsByBrand,
    monthlyUsedUnitsByBrand: data.unitCounts.monthlyUsedUnitsByBrand,
  };
}
